import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Question, ModuleId, DifficultyLevel } from '../../types';
import { Button } from '../ui/Button';
import { QuestionRendererFactory } from './renderers/QuestionRendererFactory';
import { FeedbackDisplay } from './feedback/FeedbackDisplay';
import { PracticeHeaderMinimal } from './PracticeHeaderMinimal';
import { QuestionCardClean } from './QuestionCardClean';
import { Tag } from 'lucide-react';
import { CheatSheetDrawer } from './CheatSheetDrawer';
import { ConfettiCelebration } from '../ConfettiCelebration';
import { getMotivationalMessage, getStreakMessage } from '../../utils/motivationalMessages';
import { useAppStore } from '../../hooks/useAppStore';
import { SESSION_CONFIG, XP_REWARDS } from '../../constants';
import { getLevelTier, getLevelLabel, getLevelById } from '../../config/levels';
import { getAdaptiveSession } from '../../utils/adaptiveEngine';
import { formatSubSkill } from '../../utils/formatters';
import { getQuestionTypeConfig } from '../../config/questionTypeRegistry';
interface PracticeSessionProps {
  moduleId: ModuleId | null;
  questions: Question[]; // Used for cross-module sessions (Review)
  onComplete: (summary?: {
    answeredCount: number;
  }) => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number) => void;
  cheatSheetViewed: boolean;
  onCheatSheetView: () => void;
  isCrossModule?: boolean;
  currentDifficulty?: DifficultyLevel;
}
type Phase = 'question' | 'feedback';
// Calculate XP based on difficulty and correctness
function calculateXP(isCorrect: boolean, difficulty?: DifficultyLevel): number {
  if (!isCorrect) return XP_REWARDS.EFFORT;
  if (!difficulty) return XP_REWARDS.CORRECT_EASY;
  const tier = getLevelTier(difficulty);
  switch (tier) {
    case 'hard':
      return XP_REWARDS.CORRECT_HARD;
    case 'medium':
      return XP_REWARDS.CORRECT_MEDIUM;
    case 'easy':
    default:
      return XP_REWARDS.CORRECT_EASY;
  }
}
export function PracticeSession({
  moduleId,
  questions,
  onComplete,
  onExit,
  onAnswer,
  cheatSheetViewed,
  onCheatSheetView,
  isCrossModule = false
}: PracticeSessionProps) {
  const {
    state,
    selectedGroup,
    groupVersion,
    isInitialized,
    getStartingLevel
  } = useAppStore();
  // Session state
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('question');
  const [sessionInitialized, setSessionInitialized] = useState(false);
  // Track group version to detect changes
  const lastGroupVersionRef = useRef(groupVersion);
  // Answer state
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [actualIsCorrect, setActualIsCorrect] = useState<boolean | null>(null);
  const [userTypedAnswer, setUserTypedAnswer] = useState<string>('');
  const [userAnswerIndex, setUserAnswerIndex] = useState<number | null>(null);
  const [interactiveReady, setInteractiveReady] = useState(false);
  const [shouldValidateInteractive, setShouldValidateInteractive] = useState(false);
  // UI state
  const [isCheatSheetOpen, setIsCheatSheetOpen] = useState(false);
  // Stats
  const [currentStreak, setCurrentStreak] = useState(0);
  const [motivationalText, setMotivationalText] = useState('');
  const [xpGained, setXpGained] = useState(0);
  const [showStreakCelebration, setShowStreakCelebration] = useState(false);
  const [streakMessage, setStreakMessage] = useState('');
  // Helper to check if current question is interactive
  const isInteractive = (question?: Question) => {
    if (!question) return false;
    const config = getQuestionTypeConfig(question.questionType || 'multiple_choice');
    return config.isInteractive;
  };
  // Escape key to exit
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCheatSheetOpen) {
        onExit();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onExit, isCheatSheetOpen]);
  // Initialize session questions (Adaptive) - regenerates on group change
  useEffect(() => {
    // CRITICAL: Wait for useAppStore to finish loading from localStorage
    if (!isInitialized) return;
    // Detect group change mid-session
    if (lastGroupVersionRef.current !== groupVersion) {
      lastGroupVersionRef.current = groupVersion;
      setSessionQuestions([]);
      setSessionInitialized(false);
      setCurrentQuestionIndex(0);
      setPhase('question');
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setActualIsCorrect(null);
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
      setCurrentStreak(0);
      console.log('🔄 Session fully reset due to group change');
    }
    if (!sessionInitialized) {
      if (isCrossModule) {
        setSessionQuestions(questions);
        setSessionInitialized(true);
      } else if (moduleId) {
        // Get starting level from placement/manual/auto detection
        const startingLevel = getStartingLevel(moduleId, selectedGroup);
        // Pass cross-session recently-answered IDs to avoid immediate repeats
        const recentlyAnswered = state.progress[moduleId]?.recentQuestions || [];
        console.log(`🔧 Generating session for ${moduleId} | Group ${selectedGroup} | Starting level: ${startingLevel ?? 'default'}`);
        const adaptiveSession = getAdaptiveSession(moduleId, selectedGroup, state.questionStates, state.progress[moduleId]?.subSkills || {}, SESSION_CONFIG.QUESTIONS_PER_SESSION, recentlyAnswered, startingLevel);
        if (adaptiveSession.length === 0) {
          console.log('✅ All questions for this module/group are mastered!');
        }
        setSessionQuestions(adaptiveSession);
        setSessionInitialized(true);
        console.log(`📋 Session: ${adaptiveSession.length} questions for ${moduleId} (group ${selectedGroup})`);
      }
    }
  }, [moduleId, isCrossModule, questions, sessionInitialized, selectedGroup, groupVersion, isInitialized, getStartingLevel]);
  const currentQuestion = sessionQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === sessionQuestions.length - 1;
  // Reset answer state when question changes
  useEffect(() => {
    if (currentQuestion) {
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
    }
  }, [currentQuestion]);
  // Empty state - all mastered
  if (sessionInitialized && sessionQuestions.length === 0) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-id="element-1378">
        <div className="max-w-lg w-full p-8 text-center bg-white rounded-2xl shadow-xl" data-id="element-1379">
          <div className="text-4xl mb-4" data-id="element-1380">🌟</div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-1381">
            Geweldig gedaan!
          </h2>
          <p className="text-slate-600 mb-6" data-id="element-1382">
            Je hebt alle vragen voor dit vak in groep {selectedGroup} goed
            geoefend. Probeer een ander vak of kom later terug om je kennis te
            onderhouden!
          </p>
          <Button onClick={onExit} variant="primary" size="lg" data-id="element-1383">
            Terug naar overzicht
          </Button>
        </div>
      </div>;
  }
  const handleCheatSheetClose = () => {
    setIsCheatSheetOpen(false);
    if (!cheatSheetViewed) {
      onCheatSheetView();
    }
  };
  const processAnswer = (isCorrect: boolean, distractorIndex: number) => {
    const xp = calculateXP(isCorrect, currentQuestion.difficulty);
    setXpGained(xp);
    if (isCorrect) {
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      const message = getStreakMessage(newStreak);
      if (message) {
        setStreakMessage(message);
        setShowStreakCelebration(true);
      }
    } else {
      setCurrentStreak(0);
    }
    const message = getMotivationalMessage({
      isCorrect,
      streak: currentStreak,
      questionNumber: currentQuestionIndex + 1,
      totalQuestions: sessionQuestions.length
    });
    setMotivationalText(message);
    setUserAnswerIndex(distractorIndex);
    onAnswer(currentQuestion.id, isCorrect, distractorIndex);
    setSelectedOption(null);
    setPhase('feedback');
  };
  const handleInteractiveComplete = (userAnswer: string) => {
    setUserTypedAnswer(userAnswer);
    // Use registry for validation
    const config = getQuestionTypeConfig(currentQuestion.questionType || 'multiple_choice');
    const validation = config.validateAnswer(userAnswer, currentQuestion);
    const isCorrect = validation.isCorrect;
    const distractorIndex = validation.distractorIndex ?? 0;
    const displayAnswer = validation.displayAnswer || userAnswer;
    // Update display answer if formatted version provided
    if (validation.displayAnswer) {
      setUserTypedAnswer(validation.displayAnswer);
    }
    setActualIsCorrect(isCorrect);
    processAnswer(isCorrect, distractorIndex);
  };
  const handleRendererAnswer = (userAnswer: string | number) => {
    if (isInteractive(currentQuestion)) {
      handleInteractiveComplete(userAnswer as string);
    } else {
      setUserAnswerIndex(userAnswer as number);
      setSelectedOption(userAnswer as number);
    }
  };
  const submitAnswer = () => {
    if (isInteractive(currentQuestion)) {
      setShouldValidateInteractive(true);
      return;
    }
    if (userAnswerIndex === null) return;
    const isCorrect = userAnswerIndex === currentQuestion.correctAnswer;
    processAnswer(isCorrect, userAnswerIndex);
  };
  const nextQuestion = () => {
    if (isLastQuestion) {
      onComplete({
        answeredCount: sessionQuestions.length
      });
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setActualIsCorrect(null);
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
      setPhase('question');
    }
  };
  // Enter key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase !== 'question' || isCheatSheetOpen) return;
      if (e.key === 'Enter') {
        const interactive = isInteractive(currentQuestion);
        if (interactive && interactiveReady) {
          submitAnswer();
        } else if (!interactive && userAnswerIndex !== null) {
          submitAnswer();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, isCheatSheetOpen, userAnswerIndex, interactiveReady, currentQuestion]);
  if (!currentQuestion) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-1384">
        <div className="text-center" data-id="element-1385">
          <motion.div animate={{
          rotate: 360
        }} transition={{
          duration: 1,
          repeat: Infinity,
          ease: 'linear'
        }} className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4" data-id="element-1386" />
          <p className="text-slate-600" data-id="element-1387">Slimme vragen selecteren...</p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-1388">
      <ConfettiCelebration trigger={showStreakCelebration} message={streakMessage} data-id="element-1389" />
      <CheatSheetDrawer isOpen={isCheatSheetOpen} onClose={handleCheatSheetClose} moduleId={moduleId!} subSkill={currentQuestion.subSkill} data-id="element-1390" />

      {/* Minimal Header with CheatSheet Toggle */}
      <PracticeHeaderMinimal currentQuestion={currentQuestionIndex + 1} totalQuestions={sessionQuestions.length} streak={currentStreak} onExit={onExit} onToggleCheatSheet={() => setIsCheatSheetOpen(true)} isCheatSheetOpen={isCheatSheetOpen} data-id="element-1391" />

      {/* Skill/SubSkill Badge */}
      {currentQuestion.subSkill && <div className="max-w-3xl mx-auto w-full px-4 md:px-6 pt-2" data-id="element-1392">
          <div className="flex items-center gap-2 text-xs" data-id="element-1393">
            <Tag className="w-3 h-3 text-slate-400" data-id="element-1394" />
            <span className="text-slate-400 font-medium" data-id="element-1395">
              {currentQuestion.skill && <span className="capitalize" data-id="element-1396">{currentQuestion.skill}</span>}
              {currentQuestion.skill && currentQuestion.subSkill && ' → '}
              <span className="text-slate-600 font-semibold capitalize" data-id="element-1397">
                {formatSubSkill(currentQuestion.subSkill)}
              </span>
            </span>
            <span className="text-slate-300 ml-auto" data-id="element-1398">
              {getLevelLabel(currentQuestion.difficulty)}
            </span>
          </div>
        </div>}

      {/* Main Content */}
      <div className="flex-1 max-w-3xl mx-auto w-full p-4 md:p-6 flex flex-col" data-id="element-1399">
        {/* Removed Floating Button - now in header */}

        <AnimatePresence mode="wait" data-id="element-1400">
          {phase === 'question' && <motion.div key={`question-${currentQuestionIndex}`} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="flex-1 flex flex-col" data-id="element-1401">
              <QuestionCardClean question={currentQuestion} visualAid={currentQuestion.visualAid} data-id="element-1402">
                <QuestionRendererFactory question={currentQuestion} onAnswer={handleRendererAnswer} onReadyChange={setInteractiveReady} shouldValidate={shouldValidateInteractive} data-id="element-1403" />
              </QuestionCardClean>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="mt-8" data-id="element-1404">
                <Button fullWidth size="lg" disabled={isInteractive(currentQuestion) ? !interactiveReady : userAnswerIndex === null} onClick={submitAnswer} className="h-14 text-lg shadow-lg hover:shadow-xl transition-shadow relative" data-id="element-1405">
                  Antwoord Controleren
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-mono bg-white/20 px-2 py-1 rounded hidden md:inline-block" data-id="element-1406">
                    Enter
                  </span>
                </Button>
              </motion.div>
            </motion.div>}

          {phase === 'feedback' && <motion.div key="feedback" initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex-1 flex flex-col" data-id="element-1407">
              <FeedbackDisplay question={currentQuestion} wasCorrect={isInteractive(currentQuestion) ? actualIsCorrect === true : userAnswerIndex !== null && userAnswerIndex === currentQuestion.correctAnswer} userAnswer={isInteractive(currentQuestion) ? userTypedAnswer : userAnswerIndex !== null ? currentQuestion.options[userAnswerIndex] : ''} userAnswerIndex={userAnswerIndex ?? undefined} motivationalText={motivationalText} xpGained={xpGained} onNext={nextQuestion} isLastQuestion={isLastQuestion} mode="full" data-id="element-1408" />
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}